import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { buildToolboxPageMap } from "../../blocks/toolboxPageMap";

/**
 * tempurpedic ProductListFilterPage body assembly.
 *
 * The page has TWO ``Toolbox`` StreamFields -- ``top_banner`` and ``body`` --
 * each rendered as a SEPARATE root with NO per-block Wagtail ``w-block-*``
 * wrapper, so each field renders with ``tagName={React.Fragment}``. The
 * full-page ProductListFilterPageTemplate renders this component twice (once
 * per ``field``); the ``field`` discriminator selects which Picked StreamField
 * to dispatch.
 *
 * Both fields are pure ``Toolbox`` (no page-specific extra keys), so the shared
 * ``buildToolboxPageMap`` map dispatches every block (with the four page/
 * request/settings-dependent overrides -- ``hardcoded_block`` / ``hotspot_block``
 * / ``breadcrumbs_block`` / ``customer_service_info_block`` -- applied via the
 * ``get_toolbox_block_ssr`` side-channel at every nesting depth).
 */
type IProps = Immutable<
    Pick<
        templates.ProductListFilterPageTemplate,
        | "page"
        | "hardcoded_ssr"
        | "hotspot_ssr"
        | "breadcrumbs_ssr"
        | "customer_service_ssr"
        | "shop_compare_ssr"
        | "tab_feature_attribute_ssr"
        | "reviews_ssr"
    > & {
        // Discriminator selecting which Picked StreamField to dispatch; the page
        // template renders this body once per field.
        field: "top_banner" | "body";
    }
>;

export const ProductListFilterPageBody = (
    props: IProps,
): React.JSX.Element | null => {
    const stream =
        props.field === "top_banner" ? props.page.top_banner : props.page.body;
    // Both fields are nullable (`null=True, blank=True`); render nothing when
    // the selected field is null/empty.
    if (!stream) {
        return null;
    }
    return (
        <StreamField
            tagName={React.Fragment}
            value={stream.value}
            components={buildToolboxPageMap(props)}
        />
    );
};
